// RADIUS on the shared policy screens (see ../adapterContract.js).
// Who = AD users, groups and OUs (the AD adapter's lookups); Where =
// Directory, network devices (empty = all), and the optional calling stations
// and service types. Fallback on; no approval or session.
//
// Moved from pages/enduser/radius-policies/CreateRadiusPolicyPage.jsx:
//  - the saved directory is matched by FQDN (RADIUS policies store
//    radius.domain only, not the directory id);
//  - an edit of a paused policy keeps it paused: the update API saves every
//    edit as a new, active version, so update() switches the new one off again;
//  - "Add policy for this device" (Network Devices) opens /pam/policies/radius/new
//    with location.state.prefillClients, or ?device=<value>, and the device is
//    filled in;
//  - policy names must be unique among RADIUS policies.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Select } from "antd";
import axios from "axios";

import { AN_MONO } from "../../../design-system/tokens";
import { AUTH_TOKEN, DOMAIN_ID, ORG_ID, REACT_APP_PAM_API } from "../../../constants";
import { createADPolicy, getAllADDomains, listADPolicies, policyAction, updateADPolicy } from "../../../service/adPolicyService";
import { deletePolicyJSON } from "../../../service/appService";
import { domainFqdn, domainLabel } from "../../enduser/ad-policies/adPolicyModel";
import {
  RADIUS_FORM_SERVICE_TYPES,
  cleanTokens,
  invalidTokens,
  isValidCallingStation,
  isValidClient,
  radiusDeviceSummary,
  serviceTypeLabel,
} from "../../enduser/radius-policies/radiusPolicyModel";
import adAdapter, { DomainField } from "./ad";
import { callService, defineAdapter, listFilters, updatedPolicyId } from "../adapterContract";
import { ErrorText, Field, TokenInput } from "../ui";
import { fieldDomId } from "../editor/draft";

const ids = () => ({ orgId: ORG_ID(), tenantId: DOMAIN_ID() });
const lower = (s) => String(s || "").trim().toLowerCase();

/* ------------------------------------------------------------------- data */

// Registered network devices, as options for the Devices field. Optional: the
// field also takes typed names, IPs and CIDRs, so a failed load is not fatal.
function fetchDeviceOptions() {
  return axios
    .post(
      `${REACT_APP_PAM_API}/network_device/ListDevices`,
      { orgId: ORG_ID(), tenantId: DOMAIN_ID(), domainId: DOMAIN_ID(), pageId: 1, pageSize: 200, search: "", MachineType: "radius" },
      { headers: { withCredentials: true, "X-Authorization": AUTH_TOKEN() } },
    )
    .then((res) => {
      const seen = new Set();
      const opts = [];
      (res?.data?.network_devices || []).forEach((d) => {
        const value = d.nas_identifier || d.ip_address;
        if (!value || seen.has(value)) return;
        seen.add(value);
        const name = d.name || d.device_type;
        opts.push({ value, label: name && name !== value ? `${value} · ${name}` : value });
      });
      return opts;
    })
    .catch(() => []);
}

const listRadius = () =>
  callService(listADPolicies, { ...ids(), policyType: "radius", filters: listFilters("radius"), page: 1, limit: 500 }).then((res) =>
    (res?.policies || res?.data || []).filter((p) => lower(p?.policyType || p?.policyJson?.policyType) === "radius"),
  );

// "Add policy for this device": router state from the Network Devices drawer,
// or ?device= (which also survives the old /createRadiusPolicy redirect).
function prefillDevices(location) {
  const fromState = location?.state?.prefillClients;
  const fromQuery = new URLSearchParams(location?.search || "").getAll("device");
  return cleanTokens([...(Array.isArray(fromState) ? fromState : []), ...fromQuery]);
}

function useRadiusData(draft, setProduct) {
  const location = useLocation();
  const editing = !!draft;
  const [domains, setDomains] = useState(null);
  const [devices, setDevices] = useState([]);
  const [names, setNames] = useState([]);

  useEffect(() => {
    let live = true;
    getAllADDomains({
      payload: ids(),
      onSuccess: (res) => live && setDomains(res?.domains || res?.data || []),
      onFailure: () => live && setDomains([]),
    });
    return () => {
      live = false;
    };
  }, []);

  // Device options and the names already taken: the editor only.
  useEffect(() => {
    if (!editing) return undefined;
    let live = true;
    fetchDeviceOptions().then((opts) => live && setDevices(opts));
    listRadius()
      .then((rows) => live && setNames(rows.map((r) => r.policyName)))
      .catch(() => live && setNames([]));
    return () => {
      live = false;
    };
  }, [editing]);

  // The name the policy opened with may be kept (an update saves a new version).
  const openedName = useRef(null);
  if (draft && openedName.current === null) openedName.current = lower(draft.policyName);

  // Prefill the device once, on a new policy.
  const prefilled = useRef(false);
  useEffect(() => {
    if (!draft || prefilled.current) return;
    prefilled.current = true;
    if (!/\/new\/?$/.test(location.pathname)) return;
    const list = prefillDevices(location);
    if (list.length && !(draft.product.clients || []).length) setProduct({ clients: list }, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!draft]);

  // Resolve the policy's directory once the list is in: by FQDN, else the
  // first one for a new policy. A stored domain no directory matches stays as
  // stored (shown, not silently moved to another directory).
  const adId = draft?.product?.adId;
  const fqdn = draft?.product?.domain;
  useEffect(() => {
    if (!draft || !domains || !domains.length) return;
    if (adId != null && domains.some((d) => d.id === adId)) return;
    const byName = fqdn ? domains.find((d) => lower(domainFqdn(d)) === lower(fqdn)) : null;
    if (byName) setProduct({ adId: byName.id, domain: domainFqdn(byName) }, { silent: true });
    else if (adId == null && !fqdn) setProduct({ adId: domains[0].id, domain: domainFqdn(domains[0]) }, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domains, adId, fqdn]);

  const takenNames = useMemo(() => names.map(lower).filter((n) => n && n !== openedName.current), [names]);
  return { domains: domains || [], domainsLoaded: domains !== null, devices, takenNames };
}

const directoryName = (product, data) => {
  const list = data?.domains || [];
  const d = list.find((x) => x.id === product.adId) || (product.domain ? list.find((x) => lower(domainFqdn(x)) === lower(product.domain)) : null);
  return d ? domainLabel(d) : product.domain || "";
};

/* -------------------------------------------------------------- sections */

const DEVICE_MESSAGE = "Use a device name, an IP, a CIDR like 10.10.0.0/24, or *.";
const STATION_MESSAGE = "Use an IP, a CIDR, a MAC address or *.";

function Where(props) {
  const { product, setProduct, errors, touch, data } = props;
  const [refused, setRefused] = useState("");
  const deviceOptions = useMemo(() => {
    const opts = [...(data?.devices || [])];
    (product.clients || []).forEach((v) => {
      if (!opts.some((o) => o.value === v)) opts.push({ value: v, label: v });
    });
    return opts;
  }, [data, product.clients]);
  // The four the form offers, plus any other RFC value a stored policy has.
  const serviceOptions = useMemo(() => {
    const opts = RADIUS_FORM_SERVICE_TYPES.map((t) => ({ value: t.value, label: t.label }));
    (product.serviceTypes || []).forEach((v) => {
      if (!opts.some((o) => o.value === v)) opts.push({ value: v, label: serviceTypeLabel(v) });
    });
    return opts;
  }, [product.serviceTypes]);

  const changeDevices = (next) => {
    const list = cleanTokens(next);
    const bad = list.filter((v) => !isValidClient(v));
    setRefused(bad.length ? `${bad[0]} isn't a device. ${DEVICE_MESSAGE}` : "");
    setProduct({ clients: list.filter(isValidClient) });
  };

  return (
    <>
      <DomainField {...props} label="Directory" errorKey="where.directory" empty="No directories yet" />
      <Field label="Devices" help="Empty means all devices. Pick a registered device or type a name, IP or CIDR." error={errors["where.clients"]}>
        <Select
          id={fieldDomId("where.clients")}
          mode="tags"
          aria-label="Devices"
          allowClear
          value={product.clients || []}
          onChange={changeDevices}
          onBlur={() => touch("where.clients")}
          options={deviceOptions}
          optionFilterProp="label"
          tokenSeparators={[",", " "]}
          placeholder="All devices"
          status={refused || errors["where.clients"] ? "error" : undefined}
          style={{ width: "100%", fontFamily: AN_MONO }}
        />
        <ErrorText>{refused}</ErrorText>
      </Field>
      <Field
        label="Calling stations"
        hint="optional"
        help="Empty means any. Reported by the client, so use it for grouping, not as a security boundary."
        error={errors["where.callingStations"]}
      >
        <TokenInput
          id={fieldDomId("where.callingStations")}
          ariaLabel="Calling stations"
          value={product.callingStations}
          onChange={(v) => setProduct({ callingStations: v })}
          onBlur={() => touch("where.callingStations")}
          validate={(v) => (isValidCallingStation(v) ? true : STATION_MESSAGE)}
          placeholder="Any station"
        />
      </Field>
      <Field label="Service types" hint="optional" help="Empty means any. OpenVPN sends Outbound; IPsec VPNs send Framed. The bridge log shows what a device sends.">
        <Select
          mode="multiple"
          aria-label="Service types"
          allowClear
          value={product.serviceTypes || []}
          onChange={(v) => setProduct({ serviceTypes: v })}
          options={serviceOptions}
          placeholder="Any"
          style={{ width: "100%" }}
        />
      </Field>
    </>
  );
}

/* ------------------------------------------------------------ words */

const listText = (list, empty) => {
  const l = cleanTokens(list);
  return l.length ? l.join(", ") : empty;
};

const whereText = (p) => {
  const parts = [listText(p.clients, "All devices")];
  if (cleanTokens(p.callingStations).length) parts.push(`calling stations ${listText(p.callingStations)}`);
  if (cleanTokens(p.serviceTypes).length) parts.push(`service types ${cleanTokens(p.serviceTypes).map(serviceTypeLabel).join(", ")}`);
  return parts.join("; ");
};

/* --------------------------------------------------------------- adapter */

const needsDirectory = (draft) => (draft.product.adId != null || draft.product.domain ? null : "Pick a directory first");
// The AD adapter's user, group and OU lookups: they read product.adId and
// product.domain, which this adapter keeps under the same names.
const whoFields = adAdapter.who.fields.map((f) => ({ ...f, disabledReason: needsDirectory }));

const PAUSED_WARNING = "Policy updated, but it couldn't be kept off. Switch it off again from the list.";

const radiusAdapter = defineAdapter({
  key: "radius",
  policyType: "radius",
  modeKey: "mfa_radius",
  labels: {
    product: "RADIUS",
    eyebrow: "RADIUS · Policies",
    listDescription: "Who needs MFA when signing in to a VPN, switch or firewall over RADIUS, and who is allowed or denied.",
    emptySentence: "Without a policy, sign-ins are allowed.",
    subject: "sign-in",
  },

  list: listRadius,
  create: ({ policyName, policyJson, force }) =>
    callService(createADPolicy, { ...ids(), policyName, policyType: "radius", generatedBy: "Manual", policyJson, ...(force ? { force: true } : {}) }).then((res) => ({
      id: typeof res?.data === "string" ? res.data : null,
      warning: res?.warning || "",
    })),
  // Every update is a new, active version with a new id. A policy that was off
  // is switched off again (for a moment between the two calls it is on).
  update: ({ id, policyName, policyJson, stored }) =>
    callService(updateADPolicy, {
      id,
      ...ids(),
      policyName,
      policyType: "radius",
      generatedBy: "Manual",
      policyJson,
      updatedBy: localStorage.getItem("userId") || "admin_user",
    }).then((res) => {
      const newId = updatedPolicyId(res, id);
      if (String(stored?.status || "") !== "Suspended") return { id: newId, warning: "" };
      return callService(policyAction, { ...ids(), action: { actionType: "status", actionIds: [newId], actionValue: "Suspended" } })
        .then(() => ({ id: newId, warning: "" }))
        .catch(() => ({ id: newId, warning: PAUSED_WARNING }));
    }),
  isActive: (row) => String(row?.status || "Approved") === "Approved",
  setActive: (row, active) =>
    callService(policyAction, { ...ids(), action: { actionType: "status", actionIds: [row.id || row.policyId], actionValue: active ? "Approved" : "Suspended" } }),
  remove: (row) => callService(deletePolicyJSON, { ...ids(), policyId: row.id || row.policyId }),

  emptyDraft: () => ({
    who: { everyone: false, users: [], groups: [], ous: [] },
    product: { adId: null, domain: "", clients: [], callingStations: [], serviceTypes: [] },
  }),
  fromPolicy: (row) => {
    const r = row?.policyJson?.radius || {};
    return {
      who: { everyone: !!r.matchAll, users: r.users || [], groups: r.groups || [], ous: r.ous || [] },
      product: {
        adId: null,
        domain: String(r.domain || ""),
        clients: cleanTokens(r.clients),
        callingStations: cleanTokens(r.callingStations),
        serviceTypes: cleanTokens(r.serviceTypes),
      },
    };
  },
  // policyJson.radius as dto.Radius reads it ("groups", one "domain" string).
  toPolicyJson: (draft, stored) => {
    const s = stored || {};
    const p = draft.product;
    const everyone = !!draft.who.everyone;
    return {
      ...s,
      policyName: draft.policyName.trim(),
      policyType: "radius",
      radius: {
        ...(s.radius || {}),
        domain: String(p.domain || "").trim(),
        users: everyone ? [] : cleanTokens(draft.who.users),
        groups: everyone ? [] : cleanTokens(draft.who.groups),
        ous: everyone ? [] : cleanTokens(draft.who.ous),
        matchAll: everyone,
        clients: cleanTokens(p.clients),
        callingStations: cleanTokens(p.callingStations),
        serviceTypes: cleanTokens(p.serviceTypes),
      },
    };
  },
  validate: (draft, data) => {
    const e = {};
    const p = draft.product;
    const name = lower(draft.policyName);
    if (name && (data?.takenNames || []).includes(name)) e["basics.policyName"] = "Another RADIUS policy has this name.";
    if (p.adId == null && !p.domain) e["where.directory"] = "Pick a directory.";
    const badDevices = invalidTokens(p.clients, isValidClient);
    if (badDevices.length) e["where.clients"] = `${badDevices[0]} isn't a device. ${DEVICE_MESSAGE}`;
    const badStations = invalidTokens(p.callingStations, isValidCallingStation);
    if (badStations.length) e["where.callingStations"] = `${badStations[0]} isn't a calling station. ${STATION_MESSAGE}`;
    return e;
  },

  useData: useRadiusData,
  Where,
  who: { everyone: true, except: true, fields: whoFields },

  listWhere: (row) => {
    const r = row?.policyJson?.radius || {};
    const st = cleanTokens(r.serviceTypes);
    return [radiusDeviceSummary(row?.policyJson), st.length ? st.map(serviceTypeLabel).join(", ") : ""].filter(Boolean).join(" · ");
  },
  sentenceWhere: (draft, data) => {
    const p = draft.product;
    const name = directoryName(p, data);
    const devices = cleanTokens(p.clients);
    const few = devices.length > 3 ? [...devices.slice(0, 2), `${devices.length - 2} more`] : devices;
    const dev = few.length ? `on ${few.length === 1 ? few[0] : `${few.slice(0, -1).join(", ")} or ${few[few.length - 1]}`}` : "on all devices";
    return [name && `in ${name}`, dev].filter(Boolean).join(" ");
  },
  reviewRows: (draft, data) => [
    { key: "directory", label: "Directory", value: directoryName(draft.product, data) || "None picked", section: "where" },
    { key: "where", label: "Where", value: whereText(draft.product), section: "where" },
  ],

  features: {
    fallback: true,
    approval: false,
    session: false,
  },
});

export default radiusAdapter;
